/**
 * Semantic design tokens — sourced from docs/ui/design-system/tokens/tokens.json v1.1.0
 * Brand primary is resolved at runtime from MUI theme.palette.primary.main
 */

export interface DsTokenSet {
  surfaceApp: string;
  surfaceRaised: string;
  surfaceSunken: string;
  surfaceInput: string;
  surfaceOverlay: string;
  textPrimary: string;
  textSecondary: string;
  textDisabled: string;
  borderDefault: string;
  borderStrong: string;
  borderFocus: string;
  statusError: string;
  statusSuccess: string;
  statusWarning: string;
  interactionHover: string;
  interactionActive: string;
  interactionDisabled: string;
  onPrimary: string;
  brandPrimarySubtle: string;
  radiusMd: number;
  radiusSm: number;
  radiusLg: number;
  radiusFull: number;
  space1: number;
  space2: number;
  space3: number;
  space4: number;
  space5: number;
  space6: number;
  spaceHalf: number;
  touchTargetMin: number;
  borderWidthDefault: number;
  borderWidthStrong: number;
  fontSizeBody: number;
  fontSizeCaption: number;
  fontWeightMedium: number;
  letterSpacingButton: number;
  opacityDisabled: number;
  opacitySubtle: number;
  elevation1: string;
  motionDurationMicro: number;
  motionDurationStandard: number;
  motionDurationSkeleton: number;
  motionEasingStandard: string;
  brandPrimaryHover: string;
}

export const lightTokens: DsTokenSet = {
  surfaceApp: '#F7F8FA',
  surfaceRaised: '#FFFFFF',
  surfaceSunken: '#F0F2F5',
  surfaceInput: '#FFFFFF',
  surfaceOverlay: 'rgba(0,0,0,0.4)',
  textPrimary: '#1A1D21',
  textSecondary: '#5E6470',
  textDisabled: '#98A2B3',
  borderDefault: '#E4E7EC',
  borderStrong: '#D0D5DD',
  borderFocus: '#065183',
  statusError: '#F04438',
  statusSuccess: '#12B76A',
  statusWarning: '#F79009',
  interactionHover: '#F0F2F5',
  interactionActive: '#E4E7EC',
  interactionDisabled: '#98A2B3',
  onPrimary: '#FFFFFF',
  brandPrimarySubtle: 'rgba(6,81,131,0.1)',
  radiusMd: 8,
  radiusSm: 4,
  radiusLg: 12,
  radiusFull: 9999,
  space1: 8,
  space2: 16,
  space3: 24,
  space4: 32,
  space5: 40,
  space6: 48,
  spaceHalf: 4,
  touchTargetMin: 44,
  borderWidthDefault: 1,
  borderWidthStrong: 2,
  fontSizeBody: 14,
  fontSizeCaption: 12,
  fontWeightMedium: 500,
  letterSpacingButton: 0.025,
  opacityDisabled: 0.5,
  opacitySubtle: 0.1,
  elevation1: '0 1px 3px rgba(0,0,0,0.1)',
  motionDurationMicro: 120,
  motionDurationStandard: 220,
  motionDurationSkeleton: 1500,
  motionEasingStandard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  brandPrimaryHover: '#054A76',
};

export const darkTokens: DsTokenSet = {
  surfaceApp: '#141619',
  surfaceRaised: '#1C1F24',
  surfaceSunken: '#252930',
  surfaceInput: '#333333',
  surfaceOverlay: 'rgba(0,0,0,0.6)',
  textPrimary: '#F5F6F7',
  textSecondary: '#9DA4AE',
  textDisabled: '#6B7280',
  borderDefault: '#2E3339',
  borderStrong: '#3D4450',
  borderFocus: '#065183',
  statusError: '#F04438',
  statusSuccess: '#12B76A',
  statusWarning: '#F79009',
  interactionHover: '#252930',
  interactionActive: '#2E3339',
  interactionDisabled: '#6B7280',
  onPrimary: '#FFFFFF',
  brandPrimarySubtle: 'rgba(6,81,131,0.1)',
  radiusMd: 8,
  radiusSm: 4,
  radiusLg: 12,
  radiusFull: 9999,
  space1: 8,
  space2: 16,
  space3: 24,
  space4: 32,
  space5: 40,
  space6: 48,
  spaceHalf: 4,
  touchTargetMin: 44,
  borderWidthDefault: 1,
  borderWidthStrong: 2,
  fontSizeBody: 14,
  fontSizeCaption: 12,
  fontWeightMedium: 500,
  letterSpacingButton: 0.025,
  opacityDisabled: 0.5,
  opacitySubtle: 0.1,
  elevation1: '0 1px 3px rgba(0,0,0,0.1)',
  motionDurationMicro: 120,
  motionDurationStandard: 220,
  motionDurationSkeleton: 1500,
  motionEasingStandard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  brandPrimaryHover: '#054A76',
};

export interface DsResolvedTokens extends DsTokenSet {
  brandPrimary: string;
}

export function resolveTokens(
  mode: 'light' | 'dark',
  brandPrimary: string
): DsResolvedTokens {
  const base = mode === 'dark' ? darkTokens : lightTokens;
  return {
    ...base,
    brandPrimary,
    borderFocus: brandPrimary,
  };
}
